import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Trash2, ClipboardList, BarChart3, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { useAuth, hasRole } from "@/context/AuthContext";
import { EmptyState } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const TYPES = [
  { v: "rating", l: "Rating (1–5)" },
  { v: "multiple_choice", l: "Multiple choice" },
  { v: "yes_no", l: "Yes / No" },
  { v: "open_text", l: "Open text" },
];

export default function EventSurveys() {
  const { event } = useOutletContext();
  const { role } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState([{ id: "q1", type: "rating", text: "", options: [] }]);
  const [results, setResults] = useState(null);

  const load = () => api.get(`/events/${event.id}/surveys`).then(({ data }) => setSurveys(data));
  useEffect(() => { load(); }, [event.id]);

  const addQ = () => setQuestions([...questions, { id: `q${questions.length + 1}`, type: "rating", text: "", options: [] }]);
  const updateQ = (i, patch) => setQuestions(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const removeQ = (i) => setQuestions(questions.filter((_, j) => j !== i));

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      title,
      questions: questions.filter((q) => q.text.trim()).map((q) => ({
        id: q.id, type: q.type, text: q.text,
        options: ["multiple_choice", "yes_no"].includes(q.type)
          ? (q.type === "yes_no" ? ["Yes", "No"] : (q.options || []).filter(Boolean)) : null,
      })),
    };
    if (!payload.questions.length) return toast.error("Add at least one question");
    try {
      await api.post(`/events/${event.id}/surveys`, payload);
      toast.success("Survey created");
      setTitle(""); setQuestions([{ id: "q1", type: "rating", text: "", options: [] }]); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (sid) => { await api.delete(`/events/${event.id}/surveys/${sid}`); toast.success("Survey deleted"); load(); };
  const viewResults = async (sid) => { const { data } = await api.get(`/events/${event.id}/surveys/${sid}/results`); setResults(data); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-lg font-bold text-foreground">Post-event surveys</h2>
        {hasRole(role, "org_admin") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-md" data-testid="add-survey-btn"><Plus className="h-4 w-4 mr-1.5" /> New survey</Button></DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create survey</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="survey-form">
                <div><Label>Survey title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} required className="mt-1.5" data-testid="survey-title" placeholder="Post-Event Survey" /></div>
                <div className="space-y-3">
                  {questions.map((q, i) => (
                    <div key={i} className="rounded-lg border border-border p-3 space-y-2">
                      <div className="flex gap-2">
                        <Input value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} placeholder={`Question ${i + 1}`} data-testid={`survey-q-${i}`} />
                        {questions.length > 1 && <button type="button" onClick={() => removeQ(i)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>}
                      </div>
                      <Select value={q.type} onValueChange={(v) => updateQ(i, { type: v })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                      {q.type === "multiple_choice" && (
                        <Input value={(q.options || []).join(", ")} onChange={(e) => updateQ(i, { options: e.target.value.split(",").map((s) => s.trim()) })} placeholder="Comma-separated options" className="text-sm" />
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="rounded-md" onClick={addQ}>+ Add question</Button>
                </div>
                <DialogFooter><Button type="submit" className="rounded-md" data-testid="survey-submit">Create survey</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {surveys.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No surveys yet" description="Build a post-event survey to collect structured feedback from attendees." />
      ) : (
        <div className="space-y-3" data-testid="surveys-list">
          {surveys.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">{s.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.questions.length} questions · {s.response_count} responses</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-md" onClick={() => viewResults(s.id)} data-testid={`survey-results-${s.id}`}><BarChart3 className="h-3.5 w-3.5 mr-1" /> Results</Button>
                {hasRole(role, "org_admin") && <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{results?.survey?.title} · {results?.response_count} responses</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="survey-results-view">
            {results?.survey?.questions.map((q) => {
              const agg = results.aggregates[q.id] || {};
              return (
                <div key={q.id} className="rounded-lg border border-border p-3">
                  <p className="text-sm font-medium text-foreground mb-2">{q.text}</p>
                  {agg.type === "rating" && <p className="text-2xl font-display font-bold text-accent">{agg.average} <span className="text-sm text-muted-foreground font-normal">avg ({agg.count})</span></p>}
                  {(agg.type === "multiple_choice" || agg.type === "yes_no") && (
                    <ul className="text-sm text-muted-foreground space-y-1">{Object.entries(agg.counts || {}).map(([k, v]) => <li key={k} className="flex justify-between"><span>{k}</span><span className="font-semibold text-foreground">{v}</span></li>)}</ul>
                  )}
                  {agg.type === "open_text" && (
                    <ul className="text-sm text-muted-foreground space-y-1 max-h-40 overflow-y-auto">{(agg.answers || []).map((a, i) => <li key={i} className="border-l-2 border-border pl-2">{a}</li>)}{!agg.answers?.length && <li className="italic">No responses</li>}</ul>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
